// PlanComponents/ScheduleFlowGantt.js
import React from 'react';
import { Box, Typography, Table, TableBody, TableRow, TableCell } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { border, statusBg } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import AdviceContentBox from '../../../components/AdviceContentBox';

/**
 * ガントバーを簡単に表現するコンポーネント
 * 背景色を緑（#4caf50）に変更し、バー内の項目名は表示しません
 */
function GanttBar({ detail }) {
    return (
      <Box
        sx={{
          display: 'inline-block',
          px: 2,
          py: 1,
          background: 'linear-gradient(rgb(28, 246, 36),rgb(150, 232, 153))',
          borderRadius: 3,  // 少し丸みを強調
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
          cursor: 'default',
          minWidth: '80px',   // 幅を少し広げる
          minHeight: '15px',  // 高さを少し大きめに
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'scale(1.05)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          },
        }}
        title={detail || ''}
      />
    );
  }
  

/**
 * スケジュール・選考フロー (ガントチャート) - 横スクロール対応
 * 以前のデザイン（色・コメント含む）に戻した例
 */
export default function ScheduleFlowGantt({ timeline, adv }) {
  const theme = useTheme();

  return (
    <CardItem
      title="スケジュール・選考フロー"
      adviceContent={<AdviceContentBox adviceObj={adv.timeline} />}
      color="#8d6e63"  // カード全体のカラー
    >
      {/* timeline が配列でなければ"未設定"と表示 */}
      {!Array.isArray(timeline) || timeline.length === 0 ? (
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
          未設定
        </Typography>
      ) : (
        // 外側に横スクロールコンテナを用意
        <Box sx={{ overflowX: 'auto' }}>
          {/* Tableに minWidth 指定で横幅を固定 */}
          <Table
            size="small"
            sx={{
              minWidth: timeline.length * 120,
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: `2px solid ${border(theme).default}`,
                    fontWeight: 'bold',
                    backgroundColor: statusBg(theme).success,
                    whiteSpace: 'nowrap',
                  }}
                >
                  項目
                </TableCell>
                {timeline.map((t, i) => (
                  <TableCell
                    key={i}
                    align="center"
                    sx={{
                      borderBottom: `2px solid ${border(theme).default}`,
                      fontWeight: 'bold',
                      backgroundColor: statusBg(theme).success,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t.month}
                  </TableCell>
                ))}
              </TableRow>
              {/* タスク行の描画 */}
              {timeline.map((tm, idx) =>
                tm.tasks?.map((task, taskIdx) => {
                  const from = task.fromMonthIndex ?? idx;
                  const to = task.toMonthIndex ?? idx;
                  return (
                    <TableRow key={`${idx}-${taskIdx}`}>
                      <TableCell
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {task.label}
                      </TableCell>
                      {timeline.map((_, colIdx) => {
                        const inRange = colIdx >= from && colIdx <= to;
                        return (
                          <TableCell
                            key={colIdx}
                            align="center"
                            sx={{ position: 'relative', whiteSpace: 'nowrap' }}
                          >
                            {inRange && <GanttBar detail={task.detail} />}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </Box>
      )}
    </CardItem>
  );
}
